import { createReducer, on } from '@ngrx/store';

import { ErrorsObject } from '../../../shared/utilities/error-utility.utility';
import { getAndHandleBackendErrors } from '../../../shared/utilities/reducer.utility';
import { PayableInvoicesActions } from '../actions';
import { PayableInvoice } from '../../models';
import { UserRoleActions } from '../../../application-state/store/actions';

export interface State {
  current: PayableInvoice;
  entities: { [iri: string]: PayableInvoice };
  errors: ErrorsObject;
  loaded: boolean;
  loading: boolean;
  pagination?: {
    current?: string;
    first?: string;
    last?: string;
    next?: string;
    previous?: string;
  };
  totalItems: number;
  searchEntities: Array<string>;
}

const initialState = {
  entities: {},
  errors: {},
  loaded: false,
  loading: false,
  searchEntities: [],
  totalItems: 0,
  current: null
};

export const reducer = createReducer(
  initialState,
  on(
    PayableInvoicesActions.CreatePayableInvoice,
    PayableInvoicesActions.ReadPayableInvoice,
    PayableInvoicesActions.ReadPayableInvoices,
    PayableInvoicesActions.UpdatePayableInvoice,
    PayableInvoicesActions.MailPayableInvoice,
    state => {
      return {
        ...state,
        loading: true
      };
    }
  ),
  on(
    PayableInvoicesActions.ReadPayableInvoicesSuccess,
    PayableInvoicesActions.ReadPayableInvoiceChunkSuccess,
    (state, { response }) => {
      const hasPagination = response.hasOwnProperty('hydra:view');
      const items = response['hydra:member'];
      const totalItems = response['hydra:totalItems'];
      let pagination = {};
      let searchEntities = [];

      const entities = items.reduce(
        (entities: { [id: string]: PayableInvoice }, item) => {
          return {
            ...entities,
            [item['@id']]: item
          };
        },
        // intentionally "reset" entities for proper pagination as opposed to accumulating values
        // todo: local caching? maybe use custom MatDataSource?
        {}
        // {...state.entities}
      );

      if (response.hasQueryParam) {
        items.forEach(item => {
          searchEntities = [...searchEntities, item['@id']];
        });
      }

      // Sample
      // "@id": "/api/customers?page=1",                          ---- always available
      // "hydra:first": "/api/customers?page=1",                  ---- always available
      // "hydra:next": "/api/customers?page=2"                    ---- might be unavailable
      // "hydra:previous": "/api/customers?page=2"                ---- might be unavailable
      // "hydra:last": "/api/customers?page=2",                   ---- always available

      if (hasPagination) {
        pagination = {
          first: response['hydra:view']['hydra:first'],
          current: response['hydra:view']['@id'],
          last: response['hydra:view']['hydra:last']
        };

        if (response['hydra:view'].hasOwnProperty('hydra:previous')) {
          pagination = {
            ...pagination,
            previous: response['hydra:view']['hydra:previous']
          };
        }

        if (response['hydra:view'].hasOwnProperty('hydra:next')) {
          pagination = {
            ...pagination,
            next: response['hydra:view']['hydra:next']
          };
        }
      }

      return {
        ...state,
        entities,
        searchEntities,
        totalItems,
        pagination,
        loading: false,
        loaded: true
      };
    }
  ),
  on(
    PayableInvoicesActions.ReadPayableInvoiceSuccess,
    PayableInvoicesActions.CreatePayableInvoiceSuccess,
    PayableInvoicesActions.CopyPayableInvoiceSuccess,
    PayableInvoicesActions.CreatePayableInvoiceFromInvoiceSuccess,
    PayableInvoicesActions.UpdatePayableInvoiceSuccess,
    PayableInvoicesActions.MailPayableInvoiceSuccess,
    (state, { response }) => {
      const entities = {
        ...state.entities,
        [response['@id']]: response
      };

      return {
        ...state,
        entities,
        current: response,
        loading: false
      };
    }
  ),
  on(PayableInvoicesActions.DeletePayableInvoiceSuccess, (state, { iri }) => {
    // @ts-ignore
    const { [iri]: removedItem, ...entities } = state.entities;

    return {
      ...state,
      entities,
      errors: {},
      totalItems: state.totalItems - 1,
      loading: false
    };
  }),
  on(
    PayableInvoicesActions.CreatePayableInvoiceFail,
    PayableInvoicesActions.ReadPayableInvoicePDFFail,
    PayableInvoicesActions.ReadPayableInvoiceFail,
    PayableInvoicesActions.ReadPayableInvoicesFail,
    PayableInvoicesActions.UpdatePayableInvoiceFail,
    PayableInvoicesActions.DeletePayableInvoiceFail,
    PayableInvoicesActions.MailPayableInvoiceFail,
    (state, { type, response }) => {
      return {
        ...state,
        errors: getAndHandleBackendErrors(response),
        loading: false
      };
    }
  ),
  on(PayableInvoicesActions.ReadPayableInvoicePDFSuccess, state => {
    return {
      ...state,
      loading: false
    };
  }),

  on(PayableInvoicesActions.ResetIsLoaded, state => {
    return {
      ...state,
      loaded: false
    };
  }),
  on(PayableInvoicesActions.ResetCurrentPayableInvoice, state => {
    return {
      ...state,
      current: null
    };
  }),
  on(UserRoleActions.UserRoleCheckFail, state => ({ ...state, loading: false }))
);
